import { EventType } from "@/analytics/analytics-events";
import { obbToPlanes } from "@/hooks/use-clipping-planes";
import { selectClippingBox } from "@/store/clipping-box-selectors";
import { setClippingBox } from "@/store/clipping-box-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import {
  BoxControls,
  BoxControlsRef,
  useControlsLock,
} from "@faro-lotv/app-component-toolbox";
import { Analytics } from "@faro-lotv/foreign-observers";
import { OrientedBoundingBox } from "@faro-lotv/lotv";
import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import { Box3, Plane, Quaternion, Vector3, Vector4Tuple } from "three";

/**
 *
 * @param q The quaternion to convert
 * @returns A seralizable tuple with the quaternion values
 */
export function toVec4Tuple(q: Quaternion): Vector4Tuple {
  return [q.x, q.y, q.z, q.w];
}

export type ClipSceneToolProps = {
  /** Whether the tool should be active @default true */
  active?: boolean;

  /** The current area's bounding box */
  areaBox?: OrientedBoundingBox;

  /** The current model's bounding box, to initialize the clipbox with. */
  modelBox?: Box3;

  /** Whether the user is editing the clipping box via the controls */
  clippingBoxChanging?(changing: boolean): void;

  /** Optional callback used when the clipping planes change */
  clippingPlanesChanged?(planes: Plane[]): void;
};

/**
 * @returns An interactable gizmo that allows the user to define a clipping box
 * in the 3D scene. The clipping box is saved to store when the clipscene tool
 * is deactivated, and loaded from store when the tool is activated.
 */
export function ClipSceneTool({
  active = true,
  areaBox,
  modelBox,
  clippingBoxChanging,
  clippingPlanesChanged,
}: ClipSceneToolProps): JSX.Element | null {
  const dispatch = useAppDispatch();

  const boxControlsRef = useRef<BoxControlsRef>(null);

  const { storeClippingBox, initialClippingBox } = useCurrentClippingBox(
    areaBox,
    modelBox,
  );

  // The current clipping box used by the 3D scene
  const clippingBox = useMemo(() => {
    if (storeClippingBox) {
      // If the one in the store is defined, use it
      return storeClippingBox;
    } else if (areaBox) {
      // If the user did not change the box, but we have an area with volume
      // use that one
      return areaBox;
    } else if (active) {
      // If the tool is active, start wit the model bounding boxes
      return initialClippingBox;
    }
  }, [active, areaBox, storeClippingBox, initialClippingBox]);

  // Effect run when the clipping planes change to initialize a new default clipping box
  // Here a layout effect is used, instead of a useEffect. In this way, if an area clipping
  // box is active, the clipping planes are updated before anything is rendered, preventing
  // any 3D models outside the box to flicker for an instant.
  useLayoutEffect(() => {
    clippingPlanesChanged?.(clippingBox ? obbToPlanes(clippingBox) : []);
  }, [clippingBox, clippingPlanesChanged]);

  // When the user is editing the clipping box, the default controls should be disabled
  const [isEditingClippingBox, setIsEditingClippingBox] = useState(false);
  useControlsLock(isEditingClippingBox);

  // Every time the user finishes an edit to the clipbox, the clipbox is written to the store.
  const clipboxEditFinished = useCallback(() => {
    Analytics.track(EventType.editClippingBox);

    setIsEditingClippingBox(false);
    clippingBoxChanging?.(false);
    const box = boxControlsRef.current;
    if (!box) return;
    dispatch(
      setClippingBox({
        position: box.pos.toArray(),
        size: box.size.toArray(),
        rotation: toVec4Tuple(box.rot),
      }),
    );
  }, [dispatch, clippingBoxChanging]);

  const clipboxEditStarted = useCallback(() => {
    setIsEditingClippingBox(true);
    clippingBoxChanging?.(true);
  }, [setIsEditingClippingBox, clippingBoxChanging]);

  if (!active || !clippingBox) return null;

  return (
    <BoxControls
      ref={boxControlsRef}
      onInteractionStarted={clipboxEditStarted}
      onInteractionStopped={clipboxEditFinished}
      size={clippingBox.size}
      position={clippingBox.position}
      rotation={clippingBox.quaternion}
      enableRotateX={false}
      enableRotateY={false}
      clipInside={false}
      clippingPlanesChanged={clippingPlanesChanged}
    />
  );
}

type CurrentClippingBox = {
  /** The clipping box present in the store */
  storeClippingBox: OrientedBoundingBox | undefined;
  /** The active clipping box, which may not yet be set in the store */
  initialClippingBox: OrientedBoundingBox | undefined;
};

/**
 *
 * @param areaBox the current area's bounding box
 * @param modelBox the current model's bounding box, to initialize the clipbox with.
 * @returns the current clipping box, and the initial clipping box
 */
export function useCurrentClippingBox(
  areaBox: OrientedBoundingBox | undefined,
  modelBox: Box3 | undefined,
): CurrentClippingBox {
  const clippingBox = useAppSelector(selectClippingBox);

  const storeClippingBox = useMemo(
    () =>
      clippingBox
        ? {
            size: new Vector3().fromArray(clippingBox.size),
            quaternion: new Quaternion().fromArray(clippingBox.rotation),
            position: new Vector3().fromArray(clippingBox.position),
          }
        : undefined,
    [clippingBox],
  );

  // The initial clipping box is either the one saved in the store, or
  // the one provided in the initialization prop
  const initialClippingBox = useMemo<OrientedBoundingBox | undefined>(() => {
    if (areaBox) {
      return areaBox;
    }
    if (modelBox && !modelBox.isEmpty()) {
      return {
        size: modelBox.getSize(new Vector3()),
        quaternion: new Quaternion(),
        position: modelBox.getCenter(new Vector3()),
      };
    }
  }, [areaBox, modelBox]);

  return {
    storeClippingBox,
    initialClippingBox,
  };
}
